import { useEffect, useMemo } from 'react';

import { capitalize, isNil, omitBy } from 'lodash';
import { useLocation, useMatch } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { useMedia } from '@pkg/hooks';
import { useSearchParams } from '@pkg/hooks/ReactRouter';

import {
    type GroupItemOwnership,
    type GroupsFilters,
    type GroupsItemsFilterQuery,
} from '@app/client/common/structs/groups/groups';

export const GROUPS_HOME_SEARCH_PARAMS_KEYS = {
    filter: 'filter',
    ownership: 'ownership',
    min_floor_price: 'min_floor_price',
    max_floor_price: 'max_floor_price',
    collections: 'collections',
    members: 'members',
} as const;

export const GROUPS_OWNERSHIPS: ReadonlyArray<GroupItemOwnership> = ['Minted', 'Purchased', 'Sold', 'Transferred'];
export const GROUPS_FILTERS: ReadonlyArray<GroupsFilters> = ['following', 'trending', 'popular'];

const isValidOwnership = (ownership: string): ownership is GroupItemOwnership => {
    return GROUPS_OWNERSHIPS.includes(ownership as never);
};

const isValidFilter = (filter: string | null): GroupsFilters | undefined => {
    if (filter && GROUPS_FILTERS.includes(filter as never)) return filter as GroupsFilters;
};

type UseGroupsFilterParams = {
    filters: GroupsItemsFilterQuery | undefined;
    filtersExcDefault: GroupsItemsFilterQuery | undefined;
    filtersExcType: GroupsItemsFilterQuery | undefined;
};

export const useGroupsFilterParams = (): UseGroupsFilterParams => {
    const [searchParams] = useSearchParams();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [searchParams]);

    return useMemo(() => {
        const filterTypeValue = isValidFilter(searchParams.get(GROUPS_HOME_SEARCH_PARAMS_KEYS.filter));

        const ownershipValue: GroupsItemsFilterQuery['ownership'] | undefined = searchParams
            .getAll(GROUPS_HOME_SEARCH_PARAMS_KEYS.ownership)
            .flatMap((e) => e.split(','))
            .map(capitalize)
            .filter((item): item is GroupItemOwnership => isValidOwnership(item));

        const minFloorPrice = searchParams.get(GROUPS_HOME_SEARCH_PARAMS_KEYS.min_floor_price);
        const maxFloorPrice = searchParams.get(GROUPS_HOME_SEARCH_PARAMS_KEYS.max_floor_price);

        const collectionsValues: GroupsItemsFilterQuery['contract_addresses'] | undefined = searchParams
            .getAll(GROUPS_HOME_SEARCH_PARAMS_KEYS.collections)
            .flatMap((e) => e.split(','));

        /**
         * filters is the object that will be returned.
         * We remove object keys if values are undefined.
         */
        const filters = omitBy<GroupsItemsFilterQuery>(
            {
                type: filterTypeValue,
                ownership: ownershipValue.length ? ownershipValue : undefined,
                contract_addresses: collectionsValues.length ? collectionsValues : undefined,
                min_floor_price: minFloorPrice || undefined,
                max_floor_price: maxFloorPrice || undefined,
                spam: false,
            },
            isNil,
        );

        /**
         * filtersExcDefault is also returned
         * With no "type" returned if it's value is "following"
         */

        const filtersExcDefault = omitBy(filters, (value, key) => {
            return value === 'following' && key === 'type';
        });
        const filtersExcType = omitBy(filters, (value, key) => {
            return key === 'type';
        });

        return { filters, filtersExcType, filtersExcDefault };
    }, [searchParams]);
};

export const PODS_SEARCH_PARAMS_KEYS = {
    tab: 'tab',
    ownership: 'ownership',
    min_floor_price: 'min_floor_price',
    max_floor_price: 'max_floor_price',
    collections: 'collections',
    members: 'members',
} as const;

// usePodsTabsParams is duplicate of the above, remove useGroupsFilterParams when its no longer in use
export const usePodsTabsParams = (): UseGroupsFilterParams => {
    const [searchParams] = useSearchParams();

    const podsBase = `/pods`;
    const pathname = useLocation().pathname;
    const podIndexMatch = useMatch({ path: podsBase, end: true });

    const tabPath = podIndexMatch ? 'popular' : pathname.replace(`${podsBase}/`, '');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [searchParams]);

    return useMemo(() => {
        const filterTypeValue = isValidFilter(tabPath);
        const ownershipValue: GroupsItemsFilterQuery['ownership'] | undefined = searchParams
            .getAll(PODS_SEARCH_PARAMS_KEYS.ownership)
            .flatMap((e) => e.split(','))
            .map(capitalize)
            .filter((item): item is GroupItemOwnership => isValidOwnership(item));

        const minFloorPrice = searchParams.get(PODS_SEARCH_PARAMS_KEYS.min_floor_price);
        const maxFloorPrice = searchParams.get(PODS_SEARCH_PARAMS_KEYS.max_floor_price);

        const collectionsValues: GroupsItemsFilterQuery['contract_addresses'] | undefined = searchParams
            .getAll(PODS_SEARCH_PARAMS_KEYS.collections)
            .flatMap((e) => e.split(','));

        /**
         * filters is the object that will be returned.
         * We remove object keys if values are undefined.
         */
        const filters = omitBy<GroupsItemsFilterQuery>(
            {
                type: filterTypeValue || 'popular',
                ownership: ownershipValue.length ? ownershipValue : undefined,
                contract_addresses: collectionsValues.length ? collectionsValues : undefined,
                min_floor_price: minFloorPrice || undefined,
                max_floor_price: maxFloorPrice || undefined,
                spam: false,
            },
            isNil,
        );

        /**
         * filtersExcDefault is also returned
         * With no "type" returned if it's value is "following"
         */

        const filtersExcDefault = omitBy(filters, (value, key) => {
            return value === 'following' && key === 'type';
        });
        const filtersExcType = omitBy(filters, (value, key) => {
            return key === 'type';
        });

        return { filters, filtersExcType, filtersExcDefault };
    }, [searchParams, tabPath]);
};

export const useGroupsChipFilters = () => {
    const [searchParams] = useSearchParams();

    const theme = useTheme();

    const isMobile = useMedia(`(max-width: ${theme.breakpoints.mobile})`);

    return useMemo(() => {
        const ownershipValue: GroupsItemsFilterQuery['ownership'] | undefined = searchParams
            .getAll(GROUPS_HOME_SEARCH_PARAMS_KEYS.ownership)
            .flatMap((e) => e.split(','))
            .map(capitalize)
            .filter((item): item is GroupItemOwnership => isValidOwnership(item));

        const minFloorPrice = searchParams.get(GROUPS_HOME_SEARCH_PARAMS_KEYS.min_floor_price);
        const maxFloorPrice = searchParams.get(GROUPS_HOME_SEARCH_PARAMS_KEYS.max_floor_price);
        const filterTypeValue = isValidFilter(searchParams.get(GROUPS_HOME_SEARCH_PARAMS_KEYS.filter));

        const collectionsValues: GroupsItemsFilterQuery['contract_addresses'] | undefined = searchParams
            .getAll(GROUPS_HOME_SEARCH_PARAMS_KEYS.collections)
            .flatMap((e) => e.split(','));

        const membersValues = searchParams
            .getAll(GROUPS_HOME_SEARCH_PARAMS_KEYS.members)
            .flatMap((member) => member.split(','));

        let floorPriceLabel: string | undefined;

        if (minFloorPrice && maxFloorPrice) floorPriceLabel = `${minFloorPrice} - ${maxFloorPrice}`;
        if (minFloorPrice && !maxFloorPrice) floorPriceLabel = `${minFloorPrice} -`;
        if (!minFloorPrice && maxFloorPrice) floorPriceLabel = `0 - ${maxFloorPrice}`;

        const floorPriceKeys = [
            minFloorPrice ? 'min_floor_price' : undefined,
            maxFloorPrice ? 'max_floor_price' : undefined,
        ].filter(Boolean);

        const filters = [
            {
                key: 'ownership',
                values: ownershipValue,
            },
            {
                key: 'collections',
                values: collectionsValues,
            },
            {
                key: 'members',
                values: membersValues,
            },
        ];

        const floorPriceFilters = floorPriceLabel
            ? {
                  label: floorPriceLabel,
                  keys: floorPriceKeys,
              }
            : undefined;

        const filterType = filterTypeValue !== 'following' && isMobile ? filterTypeValue : undefined;

        const filtersExist =
            !!floorPriceFilters || filters.filter((filter) => filter.values.length).length > 0 || filterType;

        return { filters, floorPriceFilters, filterType, filtersExist };
    }, [searchParams, isMobile]);
};
